import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, StatCard, money, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Plus, Buildings, UserPlus, Key, PencilSimple, Package, Receipt, MagnifyingGlass } from "@phosphor-icons/react";

const empty = { name: "", contact_name: "", email: "", phone: "", address: "", country: "", no_ptn: false, client_code: "", extra_emails: [] };
const emptyLogin = { name: "", email: "", password: "" };

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const [loginClient, setLoginClient] = useState(null);
  const [clientUsers, setClientUsers] = useState([]);
  const [loginForm, setLoginForm] = useState(emptyLogin);
  const [loginBusy, setLoginBusy] = useState(false);

  const load = () => api.get("/clients").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const extra = (form.extra_emails || []).map((e) => ({ email: e.email.trim(), label: e.label || "" })).filter((e) => e.email);
    if (extra.some((e) => !/^\S+@\S+\.\S+$/.test(e.email))) return toast.error("One of the additional emails is invalid");
    try {
      const payload = { ...form, extra_emails: extra };
      if (editingId) {
        await api.patch(`/clients/${editingId}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/clients", payload);
        toast.success("Client added");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const openAdd = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name || "", contact_name: c.contact_name || "", email: c.email || "", phone: c.phone || "", address: c.address || "", country: c.country || "", no_ptn: !!c.no_ptn, client_code: c.client_code || "", extra_emails: (c.extra_emails || []).map((e) => ({ email: e.email || "", label: e.label || "" })) });
    setOpen(true);
  };

  const removeClient = async (id, name) => {
    await api.delete(`/clients/${id}`);
    toast.success(`Client ${name} deleted`);
    load();
  };

  const openLogins = async (client) => {
    setLoginClient(client);
    setLoginForm(emptyLogin);
    setClientUsers([]);
    try {
      const { data } = await api.get(`/clients/${client.id}/users`);
      setClientUsers(data);
    } catch { /* ignore */ }
  };

  const createLogin = async () => {
    if (!loginForm.name || !loginForm.email || !loginForm.password) return toast.error("Name, email and password are required");
    setLoginBusy(true);
    try {
      await api.post(`/clients/${loginClient.id}/users`, loginForm);
      toast.success("Portal login created");
      const { data } = await api.get(`/clients/${loginClient.id}/users`);
      setClientUsers(data);
      setLoginForm(emptyLogin);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoginBusy(false);
    }
  };

  const removeLogin = async (userId, email) => {
    await api.delete(`/users/${userId}`);
    toast.success(`Login ${email} removed`);
    const { data } = await api.get(`/clients/${loginClient.id}/users`);
    setClientUsers(data);
    load();
  };

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const navigate = useNavigate();
  const countries = useMemo(() => [...new Set(rows.map((c) => (c.country || "").trim()).filter(Boolean))].sort(), [rows]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    let xs = rows.filter((c) => !t || [c.name, c.contact_name, c.email, c.phone, c.country, ...(c.extra_emails || []).map((e) => e.email)].some((v) => (v || "").toLowerCase().includes(t)));
    if (filter === "b2b") xs = xs.filter((c) => c.no_ptn);
    else if (filter === "nologin") xs = xs.filter((c) => !c.login_count);
    else if (filter === "outstanding") xs = xs.filter((c) => c.outstanding > 0);
    else if (filter.startsWith("country:")) xs = xs.filter((c) => (c.country || "").trim() === filter.slice(8));
    const key = { name: (c) => (c.name || "").toLowerCase(), orders: (c) => -(c.orders_count || 0), recent: (c) => -(new Date(c.last_order_at || 0).getTime()), outstanding: (c) => -(c.outstanding || 0) }[sort];
    return [...xs].sort((a, b) => (key(a) > key(b) ? 1 : key(a) < key(b) ? -1 : 0));
  }, [rows, q, filter, sort]);
  const totals = useMemo(() => ({ clients: rows.length, b2b: rows.filter((c) => c.no_ptn).length, logins: rows.reduce((n, c) => n + (c.login_count || 0), 0), outstanding: rows.reduce((n, c) => n + (c.outstanding || 0), 0), active: rows.filter((c) => c.open_orders > 0).length }), [rows]);

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Company accounts"
        title="Clients"
        actions={<Button className="rounded-sm" onClick={openAdd} data-testid="add-client-button"><Plus size={16} className="mr-2" /> Add Client</Button>}
      />
      {rows.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="client-kpis">
          <StatCard testid="clients-total" label="Clients" value={totals.clients} icon={Buildings} tone="blue" />
          <StatCard testid="clients-active" label="With open orders" value={totals.active} icon={Package} tone="green" />
          <StatCard testid="clients-logins" label="Portal logins" value={totals.logins} icon={Key} tone="amber" />
          <StatCard testid="clients-outstanding" label="Outstanding" value={money(totals.outstanding)} icon={Receipt} tone="red" />
        </div>
      )}
      {rows.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-testid="client-toolbar">
          <div className="relative flex-1"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input className="rounded-sm pl-9" placeholder="Search name, contact, email, phone, country…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search" /></div>
          <select className="h-10 rounded-sm border border-input bg-background px-2 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="client-filter">
            <option value="all">All clients</option>
            <option value="b2b">B2B · No PTN</option>
            <option value="outstanding">With outstanding invoices</option>
            <option value="nologin">No portal login</option>
            {countries.length > 0 && <optgroup label="Country">{countries.map((c) => <option key={c} value={`country:${c}`}>{c}</option>)}</optgroup>}
          </select>
          <select className="h-10 rounded-sm border border-input bg-background px-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="client-sort">
            <option value="name">Sort: Name</option>
            <option value="recent">Sort: Last order</option>
            <option value="orders">Sort: Most orders</option>
            <option value="outstanding">Sort: Outstanding</option>
          </select>
        </div>
      )}
      {!loading && rows.length === 0 ? (
        <EmptyState title="No clients yet" hint="Add a client company, then issue their portal login credentials from the client card." action={<Button className="rounded-sm" onClick={openAdd}>Add Client</Button>} />
      ) : shown.length === 0 ? (
        <EmptyState title="No clients match" hint="Try another search or clear the filter." action={<Button variant="outline" className="rounded-sm" onClick={() => { setQ(""); setFilter("all"); }}>Clear</Button>} />
      ) : (
        <>
        <div className="hidden md:block hp-card overflow-x-auto" data-testid="client-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Contact</th><th className="text-left px-4 py-3">Country</th><th className="text-right px-4 py-3">Orders</th><th className="text-left px-4 py-3">Last order</th><th className="text-right px-4 py-3">Outstanding</th><th className="text-center px-4 py-3">Logins</th><th className="px-2 py-3" /></tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`client-card-${c.name}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2"><Buildings size={16} className="text-primary shrink-0" weight="duotone" /><span className="font-semibold">{c.name}</span>{c.no_ptn && <span className="rounded-sm border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`client-no-ptn-badge-${c.name}`}>B2B</span>}{c.client_code && <span className="rounded-sm bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-mono font-semibold" data-testid={`client-code-badge-${c.name}`}>{c.client_code}</span>}</div>
                    {c.email && <p className="text-xs text-muted-foreground mt-0.5">{c.email}{c.extra_emails?.length ? ` +${c.extra_emails.length}` : ""}</p>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground"><p>{c.contact_name || "—"}</p>{c.phone && <p className="text-xs">{c.phone}</p>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.country || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums"><button type="button" className="hover:underline" onClick={() => navigate(`/orders?client=${encodeURIComponent(c.name)}`)} data-testid={`client-orders-${c.name}`}>{c.orders_count || 0}{c.open_orders ? <span className="text-xs text-amber-700"> · {c.open_orders} open</span> : null}</button></td>
                  <td className="px-4 py-3 text-muted-foreground">{c.last_order_at ? fmtDate(c.last_order_at) : "—"}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${c.outstanding > 0 ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{c.outstanding > 0 ? money(c.outstanding) : "—"}</td>
                  <td className="px-4 py-3 text-center"><button type="button" onClick={() => openLogins(c)} className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${c.login_count ? "bg-primary/10 text-primary" : "bg-amber-50 text-amber-700"}`} data-testid={`client-login-count-${c.name}`}>{c.login_count || 0} {c.login_count === 1 ? "login" : "logins"}</button></td>
                  <td className="px-2 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0" onClick={() => openEdit(c)} data-testid={`edit-client-${c.name}`} title="Edit client"><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0" onClick={() => openLogins(c)} data-testid={`manage-login-${c.name}`} title="Portal logins"><Key size={14} /></Button>
                      <ConfirmDelete testid={`delete-client-${c.name}`} title={`Delete ${c.name}?`} description="This permanently removes the client and all its portal logins. This cannot be undone." onConfirm={() => removeClient(c.id, c.name)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-3">
          {shown.map((c) => (
            <div key={c.id} className="hp-card p-4" data-testid={`client-card-m-${c.name}`}>
              <div className="flex items-start gap-2">
                <Buildings size={18} className="text-primary mt-0.5 shrink-0" weight="duotone" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-bold truncate">{c.name}{c.no_ptn && <span className="ml-2 align-middle rounded-sm border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">B2B</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{[c.contact_name, c.email, c.country].filter(Boolean).join(" · ") || "No contact details"}</p>
                </div>
                <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${c.login_count ? "bg-primary/10 text-primary" : "bg-amber-50 text-amber-700"}`}>{c.login_count || 0} login{c.login_count === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div className="rounded-sm bg-muted/40 p-2"><p className="text-muted-foreground">Orders</p><p className="font-semibold">{c.orders_count || 0}{c.open_orders ? ` · ${c.open_orders} open` : ""}</p></div>
                <div className="rounded-sm bg-muted/40 p-2"><p className="text-muted-foreground">Last order</p><p className="font-semibold">{c.last_order_at ? fmtDate(c.last_order_at) : "—"}</p></div>
                <div className="rounded-sm bg-muted/40 p-2"><p className="text-muted-foreground">Outstanding</p><p className={`font-semibold ${c.outstanding > 0 ? "text-red-600" : ""}`}>{c.outstanding > 0 ? money(c.outstanding) : "—"}</p></div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openEdit(c)}><PencilSimple size={14} /></Button>
                <Button variant="outline" size="sm" className="rounded-sm flex-1" onClick={() => openLogins(c)}><Key size={14} className="mr-2" /> Portal Logins</Button>
                <ConfirmDelete testid={`delete-client-m-${c.name}`} title={`Delete ${c.name}?`} description="This permanently removes the client and all its portal logins. This cannot be undone." onConfirm={() => removeClient(c.id, c.name)} />
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Add / Edit client dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">Company name *</Label><Input className="mt-1.5 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Contact</Label><Input className="mt-1.5 rounded-sm" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="client-contact" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Phone</Label><Input className="mt-1.5 rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Email</Label><Input className="mt-1.5 rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Country</Label><Input className="mt-1.5 rounded-sm" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="client-country" /></div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">Address</Label><Input className="mt-1.5 rounded-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address" /></div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">Client ID code</Label><Input className="mt-1.5 rounded-sm font-mono uppercase" placeholder="e.g. HEL-NI-001" value={form.client_code} onChange={(e) => setForm({ ...form, client_code: e.target.value.toUpperCase() })} data-testid="client-code" /><p className="text-xs text-muted-foreground mt-1">Printed on A5 box labels (Box Labels tool).</p></div>
            <label className="col-span-2 flex items-start gap-3 rounded-sm border border-border bg-muted/30 p-3 cursor-pointer" data-testid="client-no-ptn-field">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={!!form.no_ptn} onChange={(e) => setForm({ ...form, no_ptn: e.target.checked })} data-testid="client-no-ptn" />
              <span><span className="text-sm font-medium">No-PTN shipments (B2B / wholesaler)</span><span className="block text-xs text-muted-foreground">Shipments for this client show a "Shipment contents" card (Dropbox link, items, qty, batch, expiry) instead of the patient (PTN) card.</span></span>
            </label>
            <div className="col-span-2 border-t border-border pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide">Additional emails</Label>
                <Button variant="outline" size="sm" className="rounded-sm h-7" onClick={() => setForm({ ...form, extra_emails: [...(form.extra_emails || []), { email: "", label: "" }] })} data-testid="add-extra-email">
                  <Plus size={12} className="mr-1" /> Add email
                </Button>
              </div>
              {(form.extra_emails || []).length === 0 && <p className="text-xs text-muted-foreground">Save more addresses (e.g. pharmacist, accounts) to choose from when emailing shipment documents.</p>}
              {(form.extra_emails || []).map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`extra-email-row-${i}`}>
                  <Input type="email" placeholder="name@company.com" className="col-span-7 rounded-sm h-9" value={e.email} onChange={(ev) => { const list = [...form.extra_emails]; list[i] = { ...list[i], email: ev.target.value }; setForm({ ...form, extra_emails: list }); }} data-testid={`extra-email-${i}`} />
                  <Input placeholder="Label (optional)" className="col-span-4 rounded-sm h-9" value={e.label} onChange={(ev) => { const list = [...form.extra_emails]; list[i] = { ...list[i], label: ev.target.value }; setForm({ ...form, extra_emails: list }); }} data-testid={`extra-email-label-${i}`} />
                  <button className="col-span-1 flex justify-center text-lg text-muted-foreground hover:text-destructive" onClick={() => setForm({ ...form, extra_emails: form.extra_emails.filter((_, j) => j !== i) })} data-testid={`remove-extra-email-${i}`}>×</button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={save} data-testid="save-client">{editingId ? "Save changes" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal logins dialog */}
      <Dialog open={!!loginClient} onOpenChange={(v) => !v && setLoginClient(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Portal Logins — {loginClient?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Existing logins — multiple accounts can be linked to this company</p>
              {clientUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No portal logins yet for this client.</p>
              ) : (
                <div className="space-y-2">
                  {clientUsers.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between gap-2 border border-border rounded-sm px-3 py-2 text-sm" data-testid={`client-user-${u.email}`}>
                      <span className="font-medium truncate">{u.name}</span>
                      <span className="text-muted-foreground truncate ml-auto">{u.email}</span>
                      <ConfirmDelete
                        testid={`delete-client-login-${u.email}`}
                        title={`Remove login ${u.email}?`}
                        description="This permanently removes this portal login. The company and its other logins are kept."
                        onConfirm={() => removeLogin(u.user_id, u.email)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2"><UserPlus size={14} /> Create new login</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">Full name</Label><Input className="mt-1.5 rounded-sm" value={loginForm.name} onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })} data-testid="client-login-name" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Email</Label><Input type="email" className="mt-1.5 rounded-sm" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="client-login-email" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Password</Label><Input className="mt-1.5 rounded-sm" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} data-testid="client-login-password" /></div>
              </div>
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={createLogin} disabled={loginBusy} data-testid="save-client-login">{loginBusy ? "Creating…" : "Create Login"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
